import {
  Box,
  Flex,
  Text,
  IconButton,
  Button,
  Center,
  Stack,
  Collapse,
  Icon,
  Link,
  Show,
  Hide,
  Popover,
  PopoverTrigger,
  PopoverContent,
  useColorModeValue,
  useDisclosure,
  VStack,
  HStack,
  Badge,
  Divider,
  Spinner,
} from '@chakra-ui/react';

import {
  HamburgerIcon,
  CloseIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@chakra-ui/icons';

import { useUser } from '../../Context';
import { useQuery } from '@apollo/client';
import { GET_MEMBER } from '../../Queries';

import { LoginModal } from '../';
import { useState } from "react";
import { Link as RouterLink } from 'react-router-dom';

const UserProfileDropdown = ({ user }) => {
  const { data: memberData, loading: memberLoading, error: memberError } = useQuery(GET_MEMBER, {
    skip: !user, // Only run query if user is logged in
    errorPolicy: 'all'
  });

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp * 1000).toLocaleDateString();
  };

  const formatMemberType = (type) => {
    if (!type) return 'N/A';
    // Convert from UPPERCASE to Title Case
    return type.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  const getMembershipStatus = (member) => {
    if (!member) return { status: 'Unknown', color: 'gray' };
    
    const now = Date.now() / 1000;
    const paidThrough = member.paid_through;
    
    if (!paidThrough) return { status: 'Inactive', color: 'red' };
    
    if (paidThrough > now) {
      return { status: 'Active', color: 'green' };
    } else {
      return { status: 'Expired', color: 'red' };
    }
  };

  return (
    <PopoverContent bg={'white'} color={'black'} p={4} minW="300px">
      <VStack spacing={3} align="stretch">
        {memberLoading ? (
          <Center>
            <Spinner size="sm" />
          </Center>
        ) : memberError ? (
          <VStack spacing={1} align="stretch">
            <Text fontSize="sm" color="red.500">
              Unable to load profile
            </Text>
            <Text fontSize="xs" color="gray.500">
              {memberError.message}
            </Text>
          </VStack>
        ) : memberData?.getMember ? (
          <>
            <VStack spacing={2} align="stretch">
              {/* Member Name and Status */}
              <HStack justify="space-between">
                <Text fontWeight="bold" fontSize="lg">
                  {memberData.getMember.member_details.member_first_name} {memberData.getMember.member_details.member_last_name}
                </Text>
                <Badge colorScheme={getMembershipStatus(memberData.getMember.member_details).color}>
                  {getMembershipStatus(memberData.getMember.member_details).status}
                </Badge>
              </HStack>
              
              {/* Member Email */}
              <Text fontSize="sm" color="gray.600">
                {memberData.getMember.member_email}
              </Text>
              
              {/* Member Since Date */}
              <Text fontSize="sm">
                <strong>Member Since:</strong> {formatDate(memberData.getMember.member_details.member_since)}
              </Text>
              
              {/* Last Renewal Date */}
              <Text fontSize="sm">
                <strong>Last Renewal:</strong> {formatDate(memberData.getMember.member_details.last_renewal_date)}
              </Text>
              
              {/* Paid Through Date */}
              <Text fontSize="sm">
                <strong>Paid Through:</strong> {formatDate(memberData.getMember.member_details.paid_through)}
              </Text>
            </VStack>
            
            <Divider />
          </>
        ) : (
          <>
            <Text fontSize="sm" color="gray.600">
              Profile information not available
            </Text>
            <Divider />
          </>
        )}
        
        <Button
          variant={'ghost'}
          colorScheme="red"
          onClick={async () => {
            try {
              const { signOut } = await import('aws-amplify/auth');
              await signOut();
            } catch (error) {
              console.error('Error signing out:', error);
            }
          }}>
          Logout
        </Button>
      </VStack>
    </PopoverContent>
  );
};

export const WithSubnavigation = () => {
  const { user } = useUser();

  const { isOpen, onToggle } = useDisclosure();
  const { isOpen: isLoginOpen, onClose: onLoginClose, onOpen: onLoginOpen } = useDisclosure();
  const [authInitialState, setAuthInitialState] = useState('signIn');
  
  // Move all useColorModeValue calls to the top
  const textColor = useColorModeValue('white', 'white');

  return (
    <Box position="sticky" top={0} w="100%" zIndex={1000} bg={{ base: "#333", md: "rgba(0,0,0,0.5)" }}>
      <Flex
        bg={"rgba(0,0,0,0.5)"}
        color={textColor}
        minH={'60px'}
        py={{ base: 2 }}
        px={{ base: 4 }}
        borderBottom={1}
        borderStyle={'solid'}
        borderColor="#FD5521"
        align={'center'}>
        <Flex
          flex={{ base: 1, lg: 'auto' }}
          ml={{ base: -2 }}
          display={{ base: 'flex', lg: 'none' }}>
          <IconButton
            onClick={onToggle}
            color="white"
            _hover={{ bg: "white", color: 'gray.800' }}
            icon={
              isOpen ? <CloseIcon w={3} h={3} /> : <HamburgerIcon w={5} h={5} />
            }
            variant={'ghost'}
            aria-label={'Toggle Navigation'}
          />
        </Flex>
        <Flex flex={{ base: 1 }} justify={{ base: 'center', lg: 'start' }}>
          <Link as={RouterLink} to="/" _hover={{ textDecoration: 'none' }}>
            <Flex flexDirection='column' w="240px">
              <Center>
                <Text color="#FFF" fontSize="30px" fontWeight="700">Golden Gun Club</Text>
              </Center>
              <Center>
                <Text color="#FFF">Watkins, CO</Text>
              </Center>
            </Flex>
          </Link>
          <Flex display={{ base: 'none', lg: 'flex' }} ml={10}>
            <DesktopNav />
          </Flex>
        </Flex>

        <Stack
          flex={{ base: 1, lg: 0 }}
          justify={'flex-end'}
          direction={'row'}
          spacing={2}>
          {user ? (
            <Popover>
              <PopoverTrigger>
                <Button
                  color={'white'}
                  _active={{
                    color: '#FD5521',
                  }}
                  fontSize={{ base: 'xs', md: 'sm' }}
                  fontWeight={400}
                  variant={'link'}
                  px={{ base: 1, md: 2 }}>
                  <Hide above="xl">Menu</Hide>
                  <Show above="xl">Logged in as {user.username}</Show>
                </Button>
              </PopoverTrigger>
              <UserProfileDropdown user={user} />
            </Popover>
          ) : (
            <Popover>
              <PopoverTrigger>
                <Button
                  fontSize={{ base: '10px', md: 'sm' }}
                  fontWeight={600}
                  color={'white'}
                  bg={'#FD5521'}
                  px={{ base: 2, md: 4 }}
                  py={{ base: 1, md: 2 }}
                  minW={{ base: 'auto', md: 'auto' }}
                  _hover={{
                    bg: '#E04A1C',
                  }}>
                  Sign Up / Login
                </Button>
              </PopoverTrigger>
              <PopoverContent bg={'white'} color={'black'} p={2} w="auto" minW="150px">
                <VStack spacing={2} align="stretch">
                  <Button
                    size="sm"
                    colorScheme="orange"
                    onClick={() => {
                      setAuthInitialState('signUp');
                      onLoginOpen();
                    }}>
                    Sign Up
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setAuthInitialState('signIn');
                      onLoginOpen();
                    }}>
                    Login
                  </Button>
                </VStack>
              </PopoverContent>
            </Popover>
          )}
        </Stack>
      </Flex>

      <Collapse in={isOpen} animateOpacity>
        <MobileNav onLoginOpen={onLoginOpen} setAuthInitialState={setAuthInitialState} />
      </Collapse>

      <LoginModal isOpen={isLoginOpen} onClose={onLoginClose} initialState={authInitialState} />
    </Box>
  );
}

const DesktopNav = () => {
  const { user } = useUser();
  const linkColor = useColorModeValue('white', 'gray.200');
  const linkHoverColor = useColorModeValue('#FD5521', 'white');
  const popoverContentBgColor = useColorModeValue('white', 'gray.800');

  const groups = user?.signInUserSession?.accessToken?.payload?.['cognito:groups'];
  const isAdmin = groups?.includes('admins') || groups?.includes('match_directors');
  const isMatchDirector = groups?.includes('admins') || groups?.includes('match_directors');

  const renderItem = (navItem) => {
    return <Box key={navItem.label}>
      <Popover trigger={'hover'} placement={'bottom-start'}>
        <PopoverTrigger>
          <Link
            p={2}
            as={RouterLink}
            to={navItem.href ? navItem.href : navItem?.children ? navItem.children[0].href : '#'}
            fontSize={'sm'}
            fontWeight={500}
            color={linkColor}
            _hover={{
              textDecoration: 'none',
              color: linkHoverColor,
            }}>
            {navItem.label}
          </Link>
        </PopoverTrigger>

        {navItem.children && (
          <PopoverContent
            border={0}
            boxShadow={'xl'}
            bg={popoverContentBgColor}
            p={4}
            rounded={'xl'}
            minW={'sm'}>
            <Stack>
              {navItem.children.map((child, index) => (
                index !== 0 && <DesktopSubNav key={child.label} {...child} />
              ))}
            </Stack>
          </PopoverContent>)}
      </Popover>
    </Box>
  }

  const renderItems = (navItems) => {
    return navItems.map((navItem) => (renderItem(navItem)))
  }

  return (
    <Stack direction={'row'} spacing={4} alignItems={'center'}>
      {renderItems([
        ...NAV_ITEMS, 
        ...isMatchDirector ? [{ label: 'Match Management', href: '/matches/manage' }] : [],
        ...isAdmin ? [{ label: 'Admin', href: '/admin' }] : []
      ])}
    </Stack>
  );
};

const DesktopSubNav = ({ label, href, subLabel, children }) => {
  const popoverContentBgColor = useColorModeValue('white', 'gray.800');
  const hoverBgColor = useColorModeValue('#FD5521', 'gray.900');

  return (
    <Popover trigger={'hover'} placement={'right'}>
      <PopoverTrigger>
        <Link
          as={RouterLink}
          to={href}
          role={'group'}
          display={'block'}
          p={2}
          rounded={'md'}
          color={'gray.900'}
          _hover={{ bg: hoverBgColor }}>
          <Stack direction={'row'} align={'center'}>
            <Box>
              <Text _groupHover={{ color: 'white' }} fontWeight={500}>{label}</Text>
              <Text fontSize={'sm'} _groupHover={{ color: 'white' }}>{subLabel}</Text>
            </Box>
            <Flex
              transition={'all .3s ease'}
              transform={'translateX(-10px)'}
              opacity={0}
              _groupHover={{ opacity: '100%', transform: 'translateX(0)' }}
              justify={'flex-end'}
              align={'center'}
              flex={1}>
              <Icon color={'white'} w={5} h={5} as={ChevronRightIcon} />
            </Flex>
          </Stack>
        </Link>
      </PopoverTrigger>
      {children && (
        <PopoverContent
          border={0}
          boxShadow={'xl'}
          bg={popoverContentBgColor}
          p={4}
          rounded={'xl'}
          minW={'sm'}>
          <Stack>
            {children.map((child, index) => (
              index !== 0 && <DesktopSubNav key={child.label} {...child} />
            ))}
          </Stack>
        </PopoverContent>)}
    </Popover>
  );
};

const MobileNav = ({ onLoginOpen, setAuthInitialState }) => {
  const { user } = useUser();
  const groups = user?.signInUserSession?.accessToken?.payload?.['cognito:groups'];
  const isAdmin = groups?.includes('admins');
  const isMatchDirector = groups?.includes('admins') || groups?.includes('match_directors');
  
  // Move useColorModeValue to the top
  const bgColor = useColorModeValue('white', 'gray.800');

  const mobileNavItems = [
    ...NAV_ITEMS,
    ...isMatchDirector ? [{ label: 'Match Management', href: '/matches/manage' }] : [],
    ...isAdmin ? [{ label: 'Admin', href: '/admin' }] : [],
    ...!user ? [
      { 
        label: 'Login', 
        href: '#',
        onClick: () => {
          setAuthInitialState('signIn');
          onLoginOpen();
        }
      },
      { label: 'Sign Up', href: '/signup' }
    ] : []
  ];

  return (
    <Stack
      bg={bgColor}
      p={4}
      display={{ lg: 'none' }}>
      {mobileNavItems.map((navItem) => (
        <MobileNavItem key={navItem.label} {...navItem} />
      ))}
    </Stack>
  );
};

const MobileNavItem = ({ label, children, href, onClick }) => {
  const [isOpenEdits, setIsOpenEdits] = useState({});
  
  // Move useColorModeValue calls to the top
  const textColor = useColorModeValue('gray.600', 'gray.200');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const toggleIsOpen = (id, isOpen) => {
    setIsOpenEdits((prevState) => {
      return { ...prevState, [id]: isOpen }
    })
  }

  const isOpen = isOpenEdits[label] ?? false;
  return (
    <Stack spacing={4} onClick={children && (() => toggleIsOpen(label, true))}>
      <Flex
        py={2}
        as={onClick ? 'button' : RouterLink}
        to={!onClick ? (href ?? '#') : undefined}
        onClick={onClick}
        justify={'space-between'}
        align={'center'}
        _hover={{
          textDecoration: 'none',
        }}>
        <Text
          fontWeight={600}
          color={textColor}>
          {label}
        </Text>
        {children && (
          <Icon
            as={ChevronDownIcon}
            transition={'all .25s ease-in-out'}
            transform={isOpen ? 'rotate(180deg)' : ''}
            w={6}
            h={6}
          />
        )}
      </Flex>

      <Collapse in={isOpen} animateOpacity style={{ marginTop: '0!important' }}>
        <Stack
          mt={2}
          pl={4}
          borderLeft={1}
          borderStyle={'solid'}
          borderColor={borderColor}
          align={'start'}>
          {children &&
            children.map((child) => (<MobileNavItem key={child.label} {...child} />))}
        </Stack>
      </Collapse>
    </Stack>
  );
};

const NAV_ITEMS = [
  {
    label: 'Facilities',
    children: [
      {
        label: 'Facilities',
        subLabel: '',
        href: '/facilities',
      },
      {
        label: 'Shotgun',
        subLabel: 'Trap, Skeet, and Sporting Clays',
        href: '/facilities/shotgun',
      },
      {
        label: 'Rifle & Pistol',
        subLabel: 'Recreational & Competitive Outdoor Shooting',
        href: '/facilities/rifle-pistol',
      },
    ],
  },
  {
    label: 'Visitors',
    href: '/visitors',
  },
  {
    label: 'Membership',
    children: [
      {
        label: 'Membership',
        subLabel: '',
        href: '/membership',
      },
      {
        label: 'Range Rules',
        subLabel: '',
        href: '/membership/range-rules',
      },
    ],
  },
  {
    label: 'Matches & Competition',
    children: [
      {
        label: 'Matches & Competition',
        subLabel: '',
        href: '/matches-competition',
      },
      {
        label: 'Cowboy Silhouette',
        subLabel: 'Western Themed Steel Target Matches',
        href: '/matches-competition/cowboy-silhouette',
        children: [
          {
            label: 'Cowboy Silhouette',
            sublabel: 'Match Information',
            href: '/matches-competition/cowboy-silhouette'
          },
          {
            label: 'Schedule',
            sublabel: 'Upcoming Matches',
            href: '/matches-competition/cowboy-silhouette/schedule'
          },
          {
            label: 'Results',
            sublabel: '',
            href: '/matches-competition/cowboy-silhouette/results'
          }
        ]
      },
      {
        label: 'CMP Highpower/Service Rifle',
        subLabel: 'Honoring Our Nation\'s Traditions',
        href: '/matches-competition/cmp-highpower-service-rifle',
        children: [
          {
            label: 'CMP Highpower/Service Rifle',
            subLabel: 'Match Information',
            href: '/matches-competition/cmp-highpower-service-rifle'
          },
          {
            label: 'Schedule',
            sublabel: 'Upcoming Matches',
            href: '/matches-competition/cmp-highpower-service-rifle/schedule'
          },
          {
            label: 'Results',
            sublabel: '',
            href: '/matches-competition/cmp-highpower-service-rifle/results'
          },
          {
            label: 'CMP Rifle Purchase Documentation',
            subLabel: 'How to Get Documentation for CMP Rifle Purchases',
            href: '/matches-competition/cmp-highpower-service-rifle/rifle-purchase',
            children: [
              {
                label: 'CMP Rifle Purchase Documentation',
                subLabel: 'Requirements and Instructions',
                href: '/matches-competition/cmp-highpower-service-rifle/rifle-purchase'
              },
              {
                label: 'Proof of Membership Form',
                subLabel: 'Generate Your Official Membership Form',
                href: '/matches-competition/cmp-highpower-service-rifle/rifle-purchase/membership-form'
              }
            ]
          }
        ]
      },
      {
        label: 'Bowling Pin Match',
        subLabel: 'Bowling With Handguns!',
        href: '/matches-competition/bowling-pin-match',
        children: [
          {
            label: 'Bowling Pin Match',
            sublabel: 'Match Information',
            href: '/matches-competition/bowling-pin-match'
          },
          {
            label: 'Schedule',
            sublabel: 'Upcoming Matches',
            href: '/matches-competition/bowling-pin-match/schedule'
          },
          {
            label: 'Results',
            sublabel: '',
            href: '/matches-competition/bowling-pin-match/results'
          }
        ]
      },
      {
        label: 'Black Powder Silhouette',
        subLabel: 'Long Range Accuracy with Traditional Firearms',
        href: '/matches-competition/black-powder-silhouette',
        children: [
          {
            label: 'Black Powder Silhouette',
            sublabel: 'Match Information',
            href: '/matches-competition/black-powder-silhouette'
          },
          {
            label: 'Schedule',
            sublabel: 'Upcoming Matches',
            href: '/matches-competition/black-powder-silhouette/schedule'
          },
          {
            label: 'Results',
            sublabel: '',
            href: '/matches-competition/black-powder-silhouette/results'
          }
        ]
      },
      {
        label: 'NRA Action Pistol',
        subLabel: 'Handgun Speed & Accuracy',
        href: '/matches-competition/nra-action-pistol',
        children: [
          {
            label: 'NRA Action Pistol',
            sublabel: 'Match Information',
            href: '/matches-competition/nra-action-pistol'
          },
          {
            label: 'Schedule',
            sublabel: 'Upcoming Matches',
            href: '/matches-competition/nra-action-pistol/schedule'
          },
          {
            label: 'Results',
            sublabel: '',
            href: '/matches-competition/nra-action-pistol/results'
          }
        ]
      },
      {
        label: 'Trap League',
        href: '/matches-competition/trap-league',
        children: [
          {
            label: 'Trap League',
            sublabel: 'Weekly League Matches',
            href: '/matches-competition/trap-league'
          },
          {
            label: 'Signup Info',
            sublabel: '',
            href: '/matches-competition/trap-league/trap-league-info'
          },
          {
            label: 'Results',
            sublabel: '',
            href: '/matches-competition/trap-league/trap-league-results'
          }
        ],

      },
      {
        label: 'Skeet League',
        href: '/matches-competition/skeet-league',
        children: [
          {
            label: 'Skeet League',
            sublabel: 'Weekly League Matches',
            href: '/matches-competition/skeet-league'
          },
          {
            label: 'Signup Info',
            sublabel: '',
            href: '/matches-competition/skeet-league/skeet-league-info'
          },
          {
            label: 'Results',
            sublabel: '',
            href: '/matches-competition/skeet-league/skeet-league-results'
          }
        ],

      },
      {
        label: '5-Stand League',
        href: '/matches-competition/five-stand-league',
        children: [
          {
            label: '5-Stand League',
            sublabel: 'Weekly League Matches',
            href: '/matches-competition/five-stand-league'
          },
          {
            label: 'Signup Info',
            sublabel: '',
            href: '/matches-competition/five-stand-league/five-stand-league-info'
          },
          {
            label: 'Results',
            sublabel: '',
            href: '/matches-competition/five-stand-league/five-stand-league-results'
          }
        ]
      },
      {
        label: 'Tournaments',
        href: '/matches-competition/tournaments',
        children: [
          {
            label: 'Tournaments',
            sublabel: 'Registered Shoots',
            href: '/matches-competition/tournaments'
          },
          {
            label: 'Signup Info',
            sublabel: '',
            href: '/matches-competition/tournaments/tournament-info'
          },
          {
            label: 'Results',
            sublabel: '',
            href: '/matches-competition/tournaments/tournament-results'
          }
        ]
      },
    ]
  },
  {
    label: 'Calendar',
    href: '/about-us/calendar',
  },
  {
    label: 'About Us',
    children: [
      {
        label: 'About Us',
        subLabel: '',
        href: '/about-us',
      },
      {
        label: 'Calendar',
        subLabel: '',
        href: '/about-us/calendar',
      },
      {
        label: 'Member Documents',
        subLabel: '',
        href: '/about-us/board-minutes-notes',
      },
      {
        label: 'Range Camera',
        subLabel: '',
        href: '/about-us/range-camera',
      },
      {
        label: 'FAQ',
        subLabel: '',
        href: '/about-us/faq',
      },
    ],
  },
];
